import React, { useState, useRef, useMemo, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  PdsBox,
  PdsCard,
  PdsMantineText,
  PdsMantineButton,
  PdsTextField,
  PdsTextArea,
  PdsLoader,
  PdsNotification,
  PdsModal,
  PdsModalHeader,
  PdsModalBody,
  PdsModalFooter,
  PdsSpinnerIcon,
  PdsPlusAddIcon,
  PdsCheckConfirmIcon,
  PdsControlPlayIcon,
  PdsLink,
  PdsGlobe2Icon,
  PdsSearchIcon,
  PdsMonitorDesktopIcon,
  PdsNetworkIcon,
  PdsShieldSecurityIcon,
  PdsTab,
  PdsCheckbox,
} from '@podium-design-system/react-components';
import { InvestigationData } from './CaseDetails';
import UrlScanInformation from '../components/UrlScanInformation';
import Pagination from '../components/Pagination';
import HomeExistingCasesPanel from '../components/HomeExistingCasesPanel';
import { isPlausibleInvestigateDomain } from '../utils/investigateDomain';
import { isPlausibleIpAddress } from '../utils/ipAddress';
import { normalizeCaseTarget } from '../utils/targetNormalization';

function toolOutcomeByKind(outcomes, kind) {
  if (!Array.isArray(outcomes)) return null;
  return outcomes.find((o) => o.tool === kind) || null;
}

function ToolOutcomeBanner({ outcome }) {
  if (!outcome || outcome.status === 'OK') return null;
  const color = outcome.status === 'TIMEOUT' || outcome.status === 'ERROR' ? 'warning' : 'neutral';
  return (
    <PdsNotification color={color} dismissible={false}>
      {outcome.message}
    </PdsNotification>
  );
}

/** Inline progress card — matches classic Preview HUD layout (header → bar → elapsed → hint → recessed log → footer). */
function HomePreviewProgressCard({
  targetLabel,
  elapsedMs,
  logLines,
  secondaryMeta,
  footerHint,
}) {
  const lines = Array.isArray(logLines) && logLines.length > 0 ? logLines : ['Starting preview…'];
  const footer =
    footerHint ||
    'Collecting SecurityTrails, crt.sh, and URLScan on the server. Large targets can take several minutes.';
  return (
    <PdsBox direction="column" alignItems="stretch" style={{ width: '100%' }}>
      <PdsCard
        border
        borderRadius="md"
        padding="md"
        color="neutral"
        contrast="min"
        className="dashboard-preview-hud"
      >
        <PdsBox direction="column" gap="md" aria-busy="true" aria-live="polite">
          <PdsBox direction="row" alignItems="center" gap="xs" style={{ flexWrap: 'wrap' }}>
            <span className="argus-type-page-heading">Preview — </span>
            <span className="argus-data">{targetLabel || '…'}</span>
          </PdsBox>
          <div className="dashboard-preview-bar-track" aria-hidden="true">
            <div className="dashboard-preview-bar-fill" />
          </div>
          <PdsMantineText type="textline" component="div" className="dashboard-preview-elapsed">
            Elapsed {String(elapsedMs).padStart(8, ' ')} ms
          </PdsMantineText>
          <PdsMantineText type="textline" color="neutral" component="div" className="argus-type-meta">
            Large targets can take several minutes — the log below updates as each step reports.
          </PdsMantineText>
          <PdsBox direction="column" gap="xs" className="dashboard-preview-console">
            {secondaryMeta ? (
              <PdsMantineText
                type="textline"
                component="div"
                className="dashboard-preview-console-line dashboard-preview-console-meta-first"
              >
                {secondaryMeta}
              </PdsMantineText>
            ) : null}
            {lines.map((line, i) => (
              <PdsMantineText
                key={`home-preview-progress-${i}`}
                type="textline"
                component="div"
                className="dashboard-preview-console-line"
              >
                {line}
              </PdsMantineText>
            ))}
          </PdsBox>
          <PdsMantineText type="textline" color="neutral" component="div" className="argus-type-meta">
            {footer}
          </PdsMantineText>
        </PdsBox>
      </PdsCard>
    </PdsBox>
  );
}

// ─── Save Case Modal ──────────────────────────────────────────────────────────

const SaveCaseModal = ({ defaultTarget, temporaryCaseId, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    target: defaultTarget || '',
    title: '',
    description: '',
    jiraId: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!formData.target.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      // When temporaryCaseId is present, the backend promotes the preview's TEMPORARY case
      // to ACTIVE without re-running any external APIs (data was already persisted during preview).
      const payload = temporaryCaseId
        ? { ...formData, temporaryCaseId }
        : formData;
      const response = await axios.post('/api/cases', payload);
      onSuccess(response.data);
    } catch (err) {
      setError('Failed to create case: ' + (err.response?.data?.message || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PdsModal show onDismiss={onClose} minWidth="460px" maxWidth="560px" minHeight="auto" maxHeight="90vh">
      <PdsModalHeader headline="Save as New Case" />
      <PdsModalBody>
        <PdsBox direction="column" gap="md" style={{ width: '100%' }}>
          {error && <PdsNotification color="warning" dismissible={false}>{error}</PdsNotification>}
          <PdsTextField
            label="URL/IP/Entity"
            placeholder="Domain, IP, or other target"
            value={formData.target}
            onChange={(e) => setFormData({ ...formData, target: e.target.value })}
            required
            stretch
          />
          <PdsTextField
            label="Title"
            placeholder="Short title for this case..."
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            stretch
          />
          <PdsTextArea
            label="Context"
            placeholder="Describe the case and node objectives..."
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={4}
            stretch
          />
          <PdsTextField
            label="JIRA Ticket (if relevant)"
            placeholder="RRP-001"
            value={formData.jiraId}
            onChange={(e) => setFormData({ ...formData, jiraId: e.target.value })}
            stretch
          />
        </PdsBox>
      </PdsModalBody>
      <PdsModalFooter>
        <PdsBox direction="row" justifyContent="end" gap="sm">
          <PdsMantineButton rank="outline" color="neutral" onClick={onClose} disabled={submitting}>
            Cancel
          </PdsMantineButton>
          <PdsMantineButton
            rank="fill"
            color="action"
            onClick={handleSubmit}
            disabled={submitting || !formData.target.trim()}
            leadingIcon={submitting ? <PdsSpinnerIcon /> : <PdsPlusAddIcon />}
          >
            {submitting ? 'Saving...' : 'Save Case'}
          </PdsMantineButton>
        </PdsBox>
      </PdsModalFooter>
    </PdsModal>
  );
};

// ─── SecurityTrails domain keyword results ───────────────────────────────────

const SecurityTrailsDomainsSection = ({
  keyword,
  domains,
  meta,
  loading,
  error,
  onPreviewDomain,
  currentPage,
  onPageChange,
}) => {
  if (!keyword?.trim()) return null;

  const totalPages = meta?.total_pages != null ? Math.max(1, Number(meta.total_pages)) : 1;

  return (
    <PdsBox direction="column" gap="sm" style={{ width: '100%' }}>
      {loading && (
        <PdsBox justifyContent="center" padding="md">
          <PdsLoader size="sm" />
        </PdsBox>
      )}
      {error && (
        <PdsNotification color="warning" dismissible={false}>
          {error}
        </PdsNotification>
      )}
      {!loading && !error && domains && domains.length === 0 && (
        <PdsMantineText type="body" color="neutral" style={{ fontStyle: 'italic' }}>
          No domains returned from SecurityTrails for &quot;{keyword.trim()}&quot;.
        </PdsMantineText>
      )}
      {!loading && !error && domains && domains.length > 0 && (
        <PdsBox direction="column" gap="xs">
          {domains.map((host) => (
            <PdsCard
              key={host}
              border
              borderRadius="sm"
              padding="sm"
              color="neutral"
              contrast="min"
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  width: '100%',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <PdsLink
                    href={`https://${host}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="argus-data"
                    style={{
                      fontSize: 'inherit',
                      wordBreak: 'break-all',
                      display: 'inline',
                    }}
                  >
                    {host}
                  </PdsLink>
                </div>
                <div
                  style={{
                    flexShrink: 0,
                    width: 108,
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                >
                  <PdsMantineButton
                    rank="fill"
                    color="action"
                    size="sm"
                    onClick={() => onPreviewDomain(host)}
                    leadingIcon={<PdsControlPlayIcon />}
                    style={{ width: '100%', minWidth: 0 }}
                  >
                    Preview
                  </PdsMantineButton>
                </div>
              </div>
            </PdsCard>
          ))}
          {totalPages > 1 && onPageChange && (
            <Pagination
              currentPage={Math.min(currentPage, totalPages)}
              totalPages={totalPages}
              onPageChange={onPageChange}
              showPageInfo={false}
              centered
            />
          )}
        </PdsBox>
      )}
    </PdsBox>
  );
};

// ─── Home Page ────────────────────────────────────────────────────────────────

const POLL_MS = 2000;
const MAX_PREVIEW_WAIT_MS = 20 * 60 * 1000;

const Home = () => {
  const navigate = useNavigate();
  const resultsRef = useRef(null);

  const [searchTerm, setSearchTerm] = useState('');
  /** 'domain' | 'keyword' | 'ip' */
  const [searchKind, setSearchKind] = useState('domain');
  /** Keyword / IP list results: which source tab is active (matches Cases investigation tabs). */
  const [listActiveTab, setListActiveTab] = useState('securitytrails');

  const [stKeywordUsed, setStKeywordUsed] = useState('');
  const [stListPage, setStListPage] = useState(1);
  const [stDomainResults, setStDomainResults] = useState(null);
  const [stMeta, setStMeta] = useState(null);
  const [stLoading, setStLoading] = useState(false);
  const [stError, setStError] = useState(null);

  const [investigationTarget, setInvestigationTarget] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [showSaveCaseModal, setShowSaveCaseModal] = useState(false);
  /**
   * Id of the TEMPORARY case created synchronously by POST /api/investigate/preview. Sent back
   * in the 202 body so Save Case can promote it to ACTIVE without re-running external APIs.
   * Reset on every new preview.
   */
  const [temporaryCaseId, setTemporaryCaseId] = useState(null);

  const [explorePreviewData, setExplorePreviewData] = useState(null);
  const [kwPreviewLoading, setKwPreviewLoading] = useState(false);
  const [kwPreviewError, setKwPreviewError] = useState(null);

  /** Mode: 'idle' | 'keyword' | 'ip' | 'preview' */
  const [mode, setMode] = useState('idle');

  const [previewJobProgress, setPreviewJobProgress] = useState([]);

  /** Optional UrlScan Search API filename explore (keyword/IP home card); not part of preview job. */
  const [filenameSearchPanelOpen, setFilenameSearchPanelOpen] = useState(false);
  const [filenameSearchToken, setFilenameSearchToken] = useState('');
  const [filenameIncludeDownloaded, setFilenameIncludeDownloaded] = useState(false);
  const [filenameSearchRows, setFilenameSearchRows] = useState([]);
  const [filenameSearchLoading, setFilenameSearchLoading] = useState(false);
  const [filenameSearchError, setFilenameSearchError] = useState(null);
  const [filenameSearchTotal, setFilenameSearchTotal] = useState(null);
  const [filenameSearchHasMore, setFilenameSearchHasMore] = useState(false);
  const [filenameSearchRan, setFilenameSearchRan] = useState(false);

  const resetFilenameSearchState = () => {
    setFilenameSearchPanelOpen(false);
    setFilenameSearchToken('');
    setFilenameIncludeDownloaded(false);
    setFilenameSearchRows([]);
    setFilenameSearchLoading(false);
    setFilenameSearchError(null);
    setFilenameSearchTotal(null);
    setFilenameSearchHasMore(false);
    setFilenameSearchRan(false);
  };

  const runHomeFilenameSearch = async () => {
    const fn = filenameSearchToken.trim();
    if (!fn) {
      setFilenameSearchError('Enter a filename or path fragment.');
      return;
    }
    setFilenameSearchLoading(true);
    setFilenameSearchError(null);
    try {
      const res = await axios.post('/api/urlscan/search/filename', {
        filename: fn,
        includeDownloadedFiles: filenameIncludeDownloaded,
      });
      setFilenameSearchRows(Array.isArray(res.data.rows) ? res.data.rows : []);
      const t = res.data.total;
      setFilenameSearchTotal(typeof t === 'number' ? t : null);
      setFilenameSearchHasMore(Boolean(res.data.hasMore));
      setFilenameSearchRan(true);
    } catch (err) {
      const detail = err.response?.data?.message || err.response?.data?.error || err.message;
      setFilenameSearchError(typeof detail === 'string' ? detail : 'Filename search failed.');
      setFilenameSearchRows([]);
      setFilenameSearchTotal(null);
      setFilenameSearchHasMore(false);
      setFilenameSearchRan(true);
    } finally {
      setFilenameSearchLoading(false);
    }
  };

  /**
   * @param {'domain' | 'keyword' | 'ip'} streamTarget — where to push partial `result` snapshots while the job runs
   */
  const pollPreviewJob = async (jobId, streamTarget = 'domain') => {
    const started = Date.now();
    setPreviewJobProgress([]);
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (Date.now() - started > MAX_PREVIEW_WAIT_MS) {
        throw new Error(
          'The preview request timed out after waiting too long. Try a narrower query, try again, or save as a case so the full node workflow can run in the background.'
        );
      }
      await new Promise((r) => setTimeout(r, POLL_MS));
      const stRes = await axios.get(`/api/investigate/preview/jobs/${jobId}`);
      const { status, result, error, progress } = stRes.data;
      if (Array.isArray(progress) && progress.length > 0) {
        setPreviewJobProgress(progress);
      }
      if (result) {
        if (streamTarget === 'domain') {
          setPreviewData(result);
        } else {
          setExplorePreviewData(result);
        }
      }
      if (status === 'COMPLETED' && result) {
        return result;
      }
      if (status === 'FAILED') {
        throw new Error(error || 'Unknown error');
      }
    }
  };

  const fetchStSearch = async (keyword, page1Based, includeIps = false) => {
    const k = normalizeCaseTarget(keyword);
    if (!k) return;
    setStLoading(true);
    setStError(null);
    try {
      const stRes = await axios.post('/api/securitytrails/domains/search', {
        keyword: k,
        page: page1Based - 1,
        includeIps,
      });
      if (stRes.data.error) {
        setStError(stRes.data.message || 'SecurityTrails search failed');
        setStDomainResults([]);
        setStMeta(null);
      } else {
        setStDomainResults(stRes.data.domains || []);
        setStMeta(stRes.data.meta || null);
        setStListPage(page1Based);
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error;
      setStError(typeof msg === 'string' ? msg : 'SecurityTrails search failed');
      setStDomainResults([]);
      setStMeta(null);
    } finally {
      setStLoading(false);
    }
  };

  const runDomainPreview = async (targetDomain) => {
    setMode('preview');
    setExplorePreviewData(null);
    setKwPreviewError(null);
    setPreviewData(null);
    setPreviewError(null);
    setPreviewJobProgress([]);
    setTemporaryCaseId(null);
    setInvestigationTarget(targetDomain);
    setPreviewLoading(true);
    try {
      const postRes = await axios.post(
        '/api/investigate/preview',
        {
          target: targetDomain,
          homeSearchKind: 'DOMAIN',
          enableSecurityTrails: true,
          enableCrtSh: true,
          enableUrlScan: true,
          enableUrlScanSearch: true,
          enablePublicWww: false,
        },
        { validateStatus: (s) => s === 202 || s < 400 }
      );
      if (postRes.status !== 202 || !postRes.data?.jobId) {
        setPreviewError('Domain preview failed: unexpected response from server.');
        return;
      }
      if (postRes.data?.caseId) {
        setTemporaryCaseId(postRes.data.caseId);
      }
      await pollPreviewJob(postRes.data.jobId, 'domain');
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || err.message;
      setPreviewError(`Domain preview failed: ${msg}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  const runKeywordInvestigation = async (term) => {
    setMode('keyword');
    setListActiveTab('securitytrails');
    setPreviewData(null);
    setPreviewError(null);
    setExplorePreviewData(null);
    setKwPreviewError(null);
    setPreviewJobProgress([]);
    resetFilenameSearchState();
    setTemporaryCaseId(null);
    setStError(null);
    setStKeywordUsed(term);
    setStListPage(1);
    setStDomainResults(null);
    setStMeta(null);
    setKwPreviewLoading(true);

    const previewBody = {
      target: term,
      homeSearchKind: 'KEYWORD',
      enableSecurityTrails: false,
      enableCrtSh: false,
      enableUrlScan: isPlausibleInvestigateDomain(term),
      enableUrlScanSearch: true,
      enablePublicWww: false,
    };

    try {
      await Promise.all([
        fetchStSearch(term, 1, false),
        (async () => {
          try {
            const postRes = await axios.post('/api/investigate/preview', previewBody, {
              validateStatus: (s) => s === 202 || s < 400,
            });
            if (postRes.status !== 202 || !postRes.data?.jobId) {
              setKwPreviewError('URLScan preview failed: unexpected response from server.');
              return;
            }
            if (postRes.data?.caseId) {
              setTemporaryCaseId(postRes.data.caseId);
            }
            await pollPreviewJob(postRes.data.jobId, 'keyword');
          } catch (err) {
            const detail = err.response?.data?.message || err.response?.data?.error || err.message;
            setKwPreviewError(typeof detail === 'string' ? detail : 'URLScan preview failed.');
          }
        })(),
      ]);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } finally {
      setKwPreviewLoading(false);
    }
  };

  const runIpInvestigation = async (term) => {
    setMode('ip');
    setListActiveTab('securitytrails');
    setPreviewData(null);
    setPreviewError(null);
    setExplorePreviewData(null);
    setKwPreviewError(null);
    setPreviewJobProgress([]);
    resetFilenameSearchState();
    setTemporaryCaseId(null);
    setStError(null);
    setStKeywordUsed(term);
    setStListPage(1);
    setStDomainResults(null);
    setStMeta(null);
    setKwPreviewLoading(true);

    const previewBody = {
      target: term,
      homeSearchKind: 'IP',
      enableSecurityTrails: true,
      enableCrtSh: false,
      enableUrlScan: false,
      enableUrlScanSearch: true,
      enablePublicWww: false,
    };

    try {
      await Promise.all([
        fetchStSearch(term, 1, true),
        (async () => {
          try {
            const postRes = await axios.post('/api/investigate/preview', previewBody, {
              validateStatus: (s) => s === 202 || s < 400,
            });
            if (postRes.status !== 202 || !postRes.data?.jobId) {
              setKwPreviewError('Preview failed: unexpected response from server.');
              return;
            }
            if (postRes.data?.caseId) {
              setTemporaryCaseId(postRes.data.caseId);
            }
            await pollPreviewJob(postRes.data.jobId, 'ip');
          } catch (err) {
            const detail = err.response?.data?.message || err.response?.data?.error || err.message;
            setKwPreviewError(typeof detail === 'string' ? detail : 'Preview failed.');
          }
        })(),
      ]);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } finally {
      setKwPreviewLoading(false);
    }
  };

  const handlePreviewStDomain = (host) => {
    runDomainPreview(host);
  };

  /**
   * Pivot home preview from a row (UrlScan, SecurityTrails, certificates, etc.): run domain preview,
   * IP preview + ST search, or keyword preview when the value is not a clean hostname.
   */
  const handlePreviewContinueInvestigation = (targetRaw) => {
    const raw = String(targetRaw ?? '').trim();
    if (!raw) return;

    const asIp = (s) => {
      const x = String(s ?? '').trim();
      return isPlausibleIpAddress(x) ? x : null;
    };

    let ip = asIp(raw);
    if (ip) {
      setSearchKind('ip');
      setSearchTerm(ip);
      setListActiveTab('securitytrails');
      runIpInvestigation(ip);
      return;
    }

    let host = raw;
    if (/^https?:\/\//i.test(raw)) {
      try {
        host = new URL(raw).hostname;
      } catch {
        const m = raw.match(/^https?:\/\/([^/?#]+)/);
        host = m ? m[1] : raw;
      }
    } else {
      const norm = normalizeCaseTarget(raw);
      host = norm.split('/')[0].split(':')[0];
    }

    ip = asIp(host);
    if (ip) {
      setSearchKind('ip');
      setSearchTerm(ip);
      setListActiveTab('securitytrails');
      runIpInvestigation(ip);
      return;
    }

    if (isPlausibleInvestigateDomain(host)) {
      setSearchKind('domain');
      setSearchTerm(host);
      runDomainPreview(host);
      return;
    }

    setSearchKind('keyword');
    setSearchTerm(raw);
    setListActiveTab('securitytrails');
    runKeywordInvestigation(raw);
  };

  const handleSaveCaseSuccess = (newCase) => {
    setShowSaveCaseModal(false);
    navigate(`/cases/${newCase.id}`, { state: { awaitingCaseIngest: true } });
  };

  const handleStartInvestigation = () => {
    const term = normalizeCaseTarget(searchTerm);
    if (!term) return;
    if (searchKind === 'domain') {
      if (!isPlausibleInvestigateDomain(term)) {
        return;
      }
      runDomainPreview(term);
      return;
    }
    if (searchKind === 'ip') {
      if (!isPlausibleIpAddress(term)) {
        return;
      }
      runIpInvestigation(term);
      return;
    }
    runKeywordInvestigation(term);
  };

  const handleKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    handleStartInvestigation();
  };

  const hasSearchTerm = searchTerm.trim().length > 0;
  const domainInputOk = !hasSearchTerm || isPlausibleInvestigateDomain(searchTerm);
  const ipInputOk = !hasSearchTerm || isPlausibleIpAddress(searchTerm);
  const startBusy = previewLoading || kwPreviewLoading;
  const canStart =
    hasSearchTerm &&
    !previewLoading &&
    !kwPreviewLoading &&
    ((searchKind === 'keyword') ||
      (searchKind === 'domain' && domainInputOk) ||
      (searchKind === 'ip' && ipInputOk));

  const keywordPlaceholder = 'Keyword or open-ended text';
  const domainPlaceholder = 'Root domain (e.g. example.com)';
  const ipPlaceholder = 'IPv4 or IPv6 address';
  const inputPlaceholder =
    searchKind === 'keyword' ? keywordPlaceholder : searchKind === 'domain' ? domainPlaceholder : ipPlaceholder;

  const domainHint =
    searchKind === 'domain' && hasSearchTerm && !isPlausibleInvestigateDomain(searchTerm)
      ? 'Enter a valid root domain (e.g. example.com). Use Keyword for text search, or IP for IPv4/IPv6 lookups.'
      : null;
  const ipHint =
    searchKind === 'ip' && hasSearchTerm && !isPlausibleIpAddress(searchTerm)
      ? 'Enter a valid IPv4 or IPv6 address (e.g. 203.0.113.4 or 2001:db8::1).'
      : null;

  const heroExplainerByKind = {
    domain: [
      'Run initial checks on a domain of interest.',
      'Add a domain to run a first search through SecurityTrails, CRT.sh and URLScan.',
      'Save it as a case if you need a full node workflow.',
    ],
    keyword: [
      'Run initial checks on domains based on a keyword wildcard search.',
      'Add any keyword to run a first search through SecurityTrails, CRT.sh and URLScan.',
      'Save any domain linked to your keyword as a case if you need a full node workflow.',
    ],
    ip: [
      'Run initial checks on what domains are associated with an IP address.',
      'Add any IPv4 or IPv6 address to run a first search through SecurityTrails.',
      'Save any domain linked to the IP address as a case if you need a full node workflow.',
    ],
  };
  const heroExplainer = heroExplainerByKind[searchKind];

  /** Keyword / IP explore: align UrlScan empty state with SecurityTrailsDomainsSection (loader → warning → italic line). */
  const kwExploreUrlScanOutcome =
    explorePreviewData && toolOutcomeByKind(explorePreviewData.toolOutcomes, 'URL_SCAN_SEARCH');
  const kwExploreUrlScanRows = explorePreviewData?.urlScanPreviewRows || [];
  const kwExploreTerm = stKeywordUsed.trim();
  const kwUrlScanSearchFailed =
    kwExploreUrlScanOutcome &&
    (kwExploreUrlScanOutcome.status === 'ERROR' || kwExploreUrlScanOutcome.status === 'TIMEOUT');

  /** Mirrors backend case search (title/target/description, nodes, findings, notes, and tool-result tables). */
  /** Debounced search term for live "existing cases" panel (fires regardless of mode). */
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  useEffect(() => {
    const tid = setTimeout(() => setDebouncedSearchTerm(searchTerm.trim()), 300);
    return () => clearTimeout(tid);
  }, [searchTerm]);

  const homeCasesDisplayTerm = debouncedSearchTerm || investigationTarget.trim() || stKeywordUsed.trim();

  const homeCaseSearchPayload = useMemo(() => {
    const raw = debouncedSearchTerm || investigationTarget.trim() || stKeywordUsed.trim();
    if (!raw) return null;
    const normalized = normalizeCaseTarget(raw);
    // Use the active search kind when a search has been started, otherwise guess from input
    const kind = mode !== 'idle' ? searchKind : (isPlausibleIpAddress(raw) ? 'ip' : 'domain');
    if (kind === 'domain') {
      return { domain: normalized, includeSubCases: true, page: 0, pageSize: 80 };
    }
    if (kind === 'ip') {
      return {
        keyword: normalized,
        ipInvestigationExactMatch: true,
        includeSubCases: true,
        page: 0,
        pageSize: 80,
      };
    }
    return { keyword: normalized, includeSubCases: true, page: 0, pageSize: 80 };
  }, [debouncedSearchTerm, mode, investigationTarget, stKeywordUsed, searchKind]);

  const landingOnly = mode === 'idle' && !previewLoading && !kwPreviewLoading;

  const homeMaskSecondaryMeta = useMemo(() => {
    if (!kwPreviewLoading || (mode !== 'keyword' && mode !== 'ip')) return null;
    return `SecurityTrails: ${
      stLoading ? 'retrieving…' : stError ? `error — ${stError}` : `${stDomainResults?.length ?? 0} domain(s) returned`
    }`;
  }, [kwPreviewLoading, mode, stLoading, stError, stDomainResults]);

  const domainPreviewInlineFooter = useMemo(() => {
    if (!(mode === 'preview' && previewLoading)) return null;
    return previewData
      ? 'Still loading additional sources — tabs below update as each step completes.'
      : 'Collecting SecurityTrails, crt.sh, and URLScan on the server. Large targets can take several minutes.';
  }, [mode, previewLoading, previewData]);

  const [domainPreviewElapsedMs, setDomainPreviewElapsedMs] = useState(0);
  useEffect(() => {
    if (mode !== 'preview' || !previewLoading) {
      setDomainPreviewElapsedMs(0);
      return undefined;
    }
    const start = performance.now();
    const id = setInterval(() => {
      setDomainPreviewElapsedMs(Math.floor(performance.now() - start));
    }, 50);
    return () => clearInterval(id);
  }, [mode, previewLoading, investigationTarget]);

  const domainPreviewLogLines =
    previewJobProgress.length > 0 ? previewJobProgress : ['Starting preview…'];

  const [kwPreviewElapsedMs, setKwPreviewElapsedMs] = useState(0);
  useEffect(() => {
    if (!kwPreviewLoading || (mode !== 'keyword' && mode !== 'ip')) {
      setKwPreviewElapsedMs(0);
      return undefined;
    }
    const start = performance.now();
    const id = setInterval(() => {
      setKwPreviewElapsedMs(Math.floor(performance.now() - start));
    }, 50);
    return () => clearInterval(id);
  }, [kwPreviewLoading, mode, stKeywordUsed]);

  return (
    <>
    <PdsBox
      direction="column"
      alignItems="center"
      style={{
        width: '100%',
        minHeight: 'calc(100vh - 64px)',
        justifyContent: landingOnly ? 'center' : 'flex-start',
        paddingBottom: 24,
        paddingTop: landingOnly ? 0 : 32,
      }}
    >

      <PdsBox direction="column" alignItems="stretch" gap="lg" className="dashboard-home-stack">
        <header className="dashboard-home-hero-header">
          <span className="dashboard-argus-mark">Argus</span>
          <div
            className="dashboard-home-hero-explainer"
            role="region"
            aria-label={
              searchKind === 'domain'
                ? 'What Argus does for domain search'
                : searchKind === 'keyword'
                  ? 'What Argus does for keyword search'
                  : 'What Argus does for IP address search'
            }
          >
            <div className="dashboard-home-hero-explainer-inner">
              <div className="dashboard-home-hero-col">
                <span className="dashboard-home-hero-icon-slot" aria-hidden>
                  <PdsSearchIcon size="sm" />
                </span>
                <p className="dashboard-home-hero-text argus-type-intro">{heroExplainer[0]}</p>
              </div>
              <div className="dashboard-home-hero-col">
                <span className="dashboard-home-hero-icon-slot" aria-hidden>
                  <PdsPlusAddIcon size="sm" />
                </span>
                <p className="dashboard-home-hero-text argus-type-intro">{heroExplainer[1]}</p>
              </div>
              <div className="dashboard-home-hero-col">
                <span className="dashboard-home-hero-icon-slot" aria-hidden>
                  <i className="fas fa-floppy-disk" />
                </span>
                <p className="dashboard-home-hero-text argus-type-intro">{heroExplainer[2]}</p>
              </div>
            </div>
          </div>
        </header>

        <PdsBox direction="column" gap="md">
          <div className="dashboard-home-scan-shell">
            <div
              className="dashboard-home-search-kind"
              role="radiogroup"
              aria-label="Search type"
            >
              <PdsMantineButton
                type="button"
                rank={searchKind === 'keyword' ? 'fill' : 'outline'}
                color="action"
                className="dashboard-home-kind-btn"
                onClick={() => setSearchKind('keyword')}
                leadingIcon={
                  <span
                    className={
                      'dashboard-home-hash-icon ' +
                      (searchKind === 'keyword'
                        ? 'dashboard-home-hash-icon--fill'
                        : 'dashboard-home-hash-icon--outline')
                    }
                    aria-hidden
                  >
                    #
                  </span>
                }
              >
                Keyword
              </PdsMantineButton>
              <PdsMantineButton
                type="button"
                rank={searchKind === 'domain' ? 'fill' : 'outline'}
                color="action"
                className="dashboard-home-kind-btn"
                onClick={() => setSearchKind('domain')}
                leadingIcon={<PdsGlobe2Icon />}
              >
                Domain
              </PdsMantineButton>
              <PdsMantineButton
                type="button"
                rank={searchKind === 'ip' ? 'fill' : 'outline'}
                color="action"
                className="dashboard-home-kind-btn"
                onClick={() => setSearchKind('ip')}
                leadingIcon={<PdsNetworkIcon />}
              >
                IP address
              </PdsMantineButton>
            </div>

            <PdsBox direction="column" gap="xs" style={{ marginTop: 12 }}>
              <PdsTextField
                placeholder={inputPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                stretch
                style={{ fontSize: '1.05rem', width: '100%' }}
                aria-label={
                  searchKind === 'keyword'
                    ? 'Keyword search'
                    : searchKind === 'domain'
                      ? 'Root domain to investigate'
                      : 'IP address to look up'
                }
              />
            </PdsBox>

            {(domainHint || ipHint) && (
              <PdsMantineText
                type="textline"
                color="neutral"
                className="argus-type-meta"
                style={{ color: '#9a6700', marginTop: 4 }}
                role="status"
              >
                {domainHint || ipHint}
              </PdsMantineText>
            )}

            <PdsBox direction="row" justifyContent="flex-start" style={{ marginTop: 12 }}>
              <PdsMantineButton
                rank="fill"
                color="action"
                onClick={handleStartInvestigation}
                disabled={!canStart}
                leadingIcon={startBusy ? <PdsSpinnerIcon /> : <PdsControlPlayIcon />}
              >
                {startBusy ? 'Starting…' : 'Start Investigation'}
              </PdsMantineButton>
            </PdsBox>

            {homeCasesDisplayTerm && homeCaseSearchPayload && (
              <div style={{ marginTop: 16 }}>
                <HomeExistingCasesPanel
                  displayTerm={homeCasesDisplayTerm}
                  searchPayload={homeCaseSearchPayload}
                />
              </div>
            )}
          </div>
        </PdsBox>

        {previewError && (
          <PdsNotification color="warning" onDismiss={() => setPreviewError(null)}>
            {previewError}
          </PdsNotification>
        )}
      </PdsBox>

      {(mode !== 'idle' || previewLoading || kwPreviewLoading || stLoading) && (
      <PdsBox
        ref={resultsRef}
        direction="column"
        alignItems="center"
        gap="md"
        style={{ width: '100%', maxWidth: mode === 'preview' ? '100%' : 1100, marginTop: 40, paddingBottom: 60 }}
      >
        {mode === 'preview' && previewLoading && (
          <PdsBox
            direction="column"
            alignItems="stretch"
            className="dashboard-home-stack"
            style={{ width: '100%', marginBottom: 8 }}
          >
            <HomePreviewProgressCard
              targetLabel={investigationTarget || searchTerm.trim() || '…'}
              elapsedMs={domainPreviewElapsedMs}
              logLines={domainPreviewLogLines}
              footerHint={domainPreviewInlineFooter || undefined}
            />
          </PdsBox>
        )}

        {(mode === 'keyword' || mode === 'ip') && (
          <PdsBox direction="column" gap="md" className="dashboard-home-stack">
            {kwPreviewLoading && (
              <HomePreviewProgressCard
                targetLabel={stKeywordUsed.trim() || '…'}
                elapsedMs={kwPreviewElapsedMs}
                logLines={previewJobProgress.length > 0 ? previewJobProgress : ['Starting preview…']}
                secondaryMeta={homeMaskSecondaryMeta}
              />
            )}
            <PdsCard
              border
              borderRadius="md"
              padding="md"
              color="neutral"
              contrast="min"
              style={{ overflow: 'hidden', width: '100%' }}
            >
              <PdsBox direction="row" alignItems="center" gap="xs" style={{ marginBottom: 12 }} flexWrap="wrap">
                <span className="argus-type-label">Query:</span>
                <span className="argus-data">&quot;{stKeywordUsed.trim()}&quot;</span>
              </PdsBox>
              <PdsBox direction="row" gap="xs" wrap="wrap" alignItems="center" style={{ borderBottom: '2px solid #e1e4e8' }}>
                <PdsTab
                  value="securitytrails"
                  selected={listActiveTab === 'securitytrails'}
                  color="action"
                  onClick={() => setListActiveTab('securitytrails')}
                  leadingIcon={<PdsSearchIcon />}
                >
                  SecurityTrails
                </PdsTab>
                <PdsTab
                  value="urlscan"
                  selected={listActiveTab === 'urlscan'}
                  color="action"
                  onClick={() => setListActiveTab('urlscan')}
                  leadingIcon={<PdsGlobe2Icon />}
                >
                  URLScan
                </PdsTab>
                <PdsTab
                  value="publicwww"
                  selected={listActiveTab === 'publicwww'}
                  color="action"
                  onClick={() => setListActiveTab('publicwww')}
                  leadingIcon={<PdsMonitorDesktopIcon />}
                >
                  PublicWWW
                </PdsTab>
                <PdsTab
                  value="crtsh"
                  selected={listActiveTab === 'crtsh'}
                  color="action"
                  onClick={() => setListActiveTab('crtsh')}
                  leadingIcon={<PdsShieldSecurityIcon />}
                >
                  Certificate Transparency (crt.sh)
                </PdsTab>
              </PdsBox>

              <PdsBox direction="column" gap="md" style={{ paddingTop: 16 }}>
                {listActiveTab === 'securitytrails' && (
                  <SecurityTrailsDomainsSection
                    keyword={stKeywordUsed}
                    domains={stDomainResults}
                    meta={stMeta}
                    loading={stLoading}
                    error={stError}
                    onPreviewDomain={handlePreviewStDomain}
                    currentPage={stListPage}
                    onPageChange={(p) => fetchStSearch(stKeywordUsed, p, mode === 'ip')}
                  />
                )}

                {listActiveTab === 'urlscan' && (
                  <PdsBox direction="column" gap="sm">
                    {kwPreviewError && (
                      <PdsNotification color="warning" dismissible={false}>
                        {kwPreviewError}
                      </PdsNotification>
                    )}
                    {kwPreviewLoading && (
                      <PdsBox justifyContent="center" padding="md">
                        <PdsLoader size="sm" />
                      </PdsBox>
                    )}
                    {!kwPreviewLoading && explorePreviewData && (
                      <>
                        {kwUrlScanSearchFailed && (
                          <PdsNotification color="warning" dismissible={false}>
                            {kwExploreUrlScanOutcome.message}
                          </PdsNotification>
                        )}
                        {!kwUrlScanSearchFailed && (
                          <UrlScanInformation
                            preloadedRows={kwExploreUrlScanRows}
                            highlightTerm={kwExploreTerm}
                            viewDensity="comfortable"
                            onInvestigate={handlePreviewContinueInvestigation}
                            emptyHint={
                              kwExploreUrlScanRows.length === 0
                                ? `No URLScan search results returned for "${kwExploreTerm}".`
                                : undefined
                            }
                          />
                        )}
                      </>
                    )}

                    {!kwPreviewLoading && (
                      <PdsBox
                          direction="column"
                          gap="sm"
                          style={{
                            marginTop: 12,
                            paddingTop: 14,
                            borderTop: '1px solid #d0d7de',
                          }}
                        >
                          <PdsMantineText type="textline" fontWeight="strong" component="div" className="argus-type-meta">
                            Filename search (URLScan)
                          </PdsMantineText>
                          <PdsMantineText type="textline" color="neutral" component="div" className="argus-type-meta" style={{ lineHeight: 1.5 }}>
                            Search historical scans where the requested URL path matches a filename (same Elasticsearch{' '}
                            <code style={{ fontSize: '0.9em' }}>filename:&quot;…&quot;</code> syntax as urlscan.io). Optionally include{' '}
                            <code style={{ fontSize: '0.9em' }}>files.filename</code> for assets downloaded during the scan.
                          </PdsMantineText>

                          {!filenameSearchPanelOpen ? (
                            <PdsMantineButton
                              rank="outline"
                              color="action"
                              size="sm"
                              type="button"
                              style={{ alignSelf: 'flex-start' }}
                              onClick={() => {
                                setFilenameSearchPanelOpen(true);
                                setFilenameSearchToken(stKeywordUsed.trim());
                                setFilenameSearchError(null);
                              }}
                            >
                              Add filename search
                            </PdsMantineButton>
                          ) : (
                            <PdsBox direction="column" gap="sm" style={{ width: '100%' }}>
                              <PdsTextField
                                label="Filename or path fragment"
                                placeholder="e.g. widgetloader"
                                value={filenameSearchToken}
                                onChange={(e) => setFilenameSearchToken(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') runHomeFilenameSearch();
                                }}
                                stretch
                              />
                              <PdsBox direction="row" alignItems="center" gap="sm" wrap="wrap">
                                <PdsCheckbox
                                  checked={filenameIncludeDownloaded}
                                  color="action"
                                  onCheckedChange={(checked) => setFilenameIncludeDownloaded(Boolean(checked))}
                                />
                                <PdsMantineText type="textline" component="span" className="argus-type-meta">
                                  Also match downloaded file names (<code style={{ fontSize: '0.9em' }}>files.filename</code>)
                                </PdsMantineText>
                              </PdsBox>
                              <PdsBox direction="row" gap="xs" wrap="wrap" alignItems="center">
                                <PdsMantineButton
                                  rank="fill"
                                  color="action"
                                  size="sm"
                                  type="button"
                                  disabled={filenameSearchLoading}
                                  leadingIcon={filenameSearchLoading ? <PdsSpinnerIcon /> : undefined}
                                  onClick={() => runHomeFilenameSearch()}
                                >
                                  {filenameSearchLoading ? 'Searching…' : 'Run filename search'}
                                </PdsMantineButton>
                                <PdsMantineButton
                                  rank="outline"
                                  color="neutral"
                                  size="sm"
                                  type="button"
                                  disabled={filenameSearchLoading}
                                  onClick={() => {
                                    resetFilenameSearchState();
                                  }}
                                >
                                  Cancel
                                </PdsMantineButton>
                              </PdsBox>
                            </PdsBox>
                          )}

                          {filenameSearchError && (
                            <PdsNotification color="warning" dismissible={false}>
                              {filenameSearchError}
                            </PdsNotification>
                          )}

                          {filenameSearchRan && !filenameSearchLoading && (
                            <>
                              <PdsMantineText type="textline" color="neutral" component="div" className="argus-type-meta">
                                Showing {filenameSearchRows.length} URLScan hit
                                {filenameSearchRows.length === 1 ? '' : 's'}
                                {filenameSearchTotal != null ? ` of ${filenameSearchTotal}` : ''}
                                {filenameSearchHasMore ? ' (more available on URLScan — paging not loaded here).' : ''}
                              </PdsMantineText>
                              <UrlScanInformation
                                preloadedRows={filenameSearchRows}
                                highlightTerm={filenameSearchToken.trim()}
                                viewDensity="comfortable"
                                onInvestigate={handlePreviewContinueInvestigation}
                                emptyHint={
                                  filenameSearchRows.length === 0 && !filenameSearchError
                                    ? `No URLScan filename matches for "${filenameSearchToken.trim()}".`
                                    : undefined
                                }
                              />
                            </>
                          )}
                      </PdsBox>
                    )}
                  </PdsBox>
                )}

                {listActiveTab === 'publicwww' && (
                  <PdsMantineText type="body" color="neutral" style={{ margin: 0, lineHeight: 1.55 }}>
                    Quick preview here covers SecurityTrails, URL Scan and Crt.sh only. PublicWWW is available as an additional search tool after saving as a case.
                  </PdsMantineText>
                )}

                {listActiveTab === 'crtsh' && (
                  <PdsMantineText type="body" color="neutral" style={{ margin: 0, lineHeight: 1.55 }}>
                    {mode === 'ip'
                      ? 'Certificate Transparency (crt.sh) does not accept IP addresses as input. Use the Domain tab with a hostname, or save as a case to run a fuller certificate review where supported.'
                      : 'Certificate Transparency (crt.sh) is tied to hostnames. This keyword preview does not load crt.sh results; open a domain preview or save as a case to query certificate data.'}
                  </PdsMantineText>
                )}
              </PdsBox>
            </PdsCard>
          </PdsBox>
        )}

        {/* Preview results */}
        {mode === 'preview' && previewData && (
          <PdsBox direction="column" gap="md" style={{ width: '100%', maxWidth: 1100, margin: '0 auto', padding: '0 16px' }}>
            {/* Save Case banner */}
            <PdsCard border borderRadius="md" padding="md" color="action" contrast="low">
              <PdsBox direction="row" alignItems="center" justifyContent="space-between" wrap="wrap" gap="sm">
                <PdsBox direction="column" gap="xs">
                  <PdsMantineText type="textline" fontWeight="strong" className="argus-type-card-title">
                    Preview results for: {previewData.targetValue}
                  </PdsMantineText>
                  <PdsMantineText type="textline" color="neutral" className="argus-type-meta">
                    {previewLoading
                      ? 'Partial preview — additional data may still load. These results are not saved until you save a case.'
                      : 'These results are not saved. Click "Save Case" to continue this node.'}
                  </PdsMantineText>
                </PdsBox>
                <PdsMantineButton
                  rank="fill"
                  color="action"
                  onClick={() => setShowSaveCaseModal(true)}
                  leadingIcon={<PdsCheckConfirmIcon />}
                >
                  Save Case
                </PdsMantineButton>
              </PdsBox>
            </PdsCard>

            {/* Only serious outcomes globally — EMPTY "no records" belongs per tab (avoids e.g. crt.sh while viewing UrlScan). */}
            {Array.isArray(previewData.toolOutcomes) &&
              previewData.toolOutcomes
                .filter((o) => o && (o.status === 'ERROR' || o.status === 'TIMEOUT'))
                .map((o) => <ToolOutcomeBanner key={o.tool} outcome={o} />)}

            {/* Reuse InvestigationData in preview mode */}
            <InvestigationData
              preloadedData={previewData}
              streamingPreview={previewLoading}
              onPreviewContinue={handlePreviewContinueInvestigation}
              onPreviewDataUpdated={setPreviewData}
            />
          </PdsBox>
        )}
      </PdsBox>
      )}

      {/* Save Case modal */}
      {showSaveCaseModal && (
        <SaveCaseModal
          defaultTarget={previewData?.targetValue || explorePreviewData?.targetValue || investigationTarget || searchTerm.trim()}
          temporaryCaseId={temporaryCaseId}
          onClose={() => setShowSaveCaseModal(false)}
          onSuccess={handleSaveCaseSuccess}
        />
      )}
    </PdsBox>

    </>
  );
};

export default Home;
